import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Link,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useDrugLabel, useDrugReference } from "hooks/useDrugReference";

// Long enough to name the hazard, short enough not to bury the card. The full text is one
// click away on DailyMed.
const BOXED_WARNING_PREVIEW_CHARS = 280;

const preview = (text) => {
  const flat = String(text).replace(/\s+/g, " ").trim();
  if (flat.length <= BOXED_WARNING_PREVIEW_CHARS) return flat;
  const cut = flat.slice(0, BOXED_WARNING_PREVIEW_CHARS);
  return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:]$/, "")}…`;
};

/**
 * The FDA label section of an expanded medication card: the dose check against the saved
 * dosage, the label's boxed warning when it has one, and a link to the full label. Rendered
 * inside the card's Collapse, so nothing is read until the patient opens the card.
 *
 * All of it comes from the drug reference database (functions/src/openfda), keyed by the rxcui
 * the verified search saved. A medication entered by hand is found by name through the brand-
 * name aliases when RxNorm knows it ("Coumadin" → warfarin); otherwise it shows only what a
 * curated entry can say about it, which is usually nothing.
 */
const MedicationLabelInfo = ({ medication, doseWarning, doseSource }) => {
  const { t } = useTranslation("medications");
  const { reference, loading } = useDrugReference(medication?.rxcui, medication?.name);
  const { label, loading: labelLoading } = useDrugLabel(reference?.labelSetId);

  if (loading || labelLoading) {
    return <CircularProgress size={20} sx={{ mb: 2 }} />;
  }

  const boxedWarning = label?.sections?.boxedWarning;
  const labelUrl = reference?.dailymedUrl || doseSource;
  if (!doseWarning && !boxedWarning && !labelUrl) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {t("labelInfo.title")}
      </Typography>

      {doseWarning && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {doseWarning}
        </Alert>
      )}

      {boxedWarning && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <AlertTitle>{t("common.boxedWarning")}</AlertTitle>
          {preview(boxedWarning)}
        </Alert>
      )}

      {labelUrl && (
        <Link href={labelUrl} target="_blank" rel="noopener noreferrer" variant="body2">
          {t("labelInfo.readOfficialLabel")}
        </Link>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        {t("labelInfo.disclaimer")}
      </Typography>
    </Box>
  );
};

export default MedicationLabelInfo;
